import { IconArrowLeft } from "@tabler/icons-react";
import {
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import type { Issue } from "@/agents/components/run-report/types";

import { AzureDevOpsResultSection as AzureDevOpsSection } from "@/agents/components/run-report/azure-devops/result-section";
import { GitHubActionsResultSection } from "@/agents/components/run-report/github-actions/result-section";
import { K8sIssuesResultSection } from "@/agents/components/run-report/k8s-issues/result-section";
import { K8sOptimizationResultSection } from "@/agents/components/run-report/k8s-optimization/result-section";
import {
  filterSuppressedK8sIssuesForDisplay,
  getK8sIssuesDisplayState,
} from "@/agents/components/run-report/k8s/filter-suppressed-issues";
import { getPodId } from "@/agents/components/run-report/k8s/pod-row";
import { GitHubIssuesWarning } from "@/agents/components/run-report/markdown/github-issues-warning";
import { OtelReceiverResultSection } from "@/agents/components/run-report/otel-receiver/result-section";
import { ErrorCard } from "@/agents/components/run-report/overview/error-card";
import { PendingMessage } from "@/agents/components/run-report/overview/pending-message";
import { RunOverviewCards } from "@/agents/components/run-report/overview/run-overview-cards";
import { SentryResultSection } from "@/agents/components/run-report/sentry/result-section";
import { useCurrentTime } from "@/hooks/use-current-time";
import { useTRPC } from "@/infrastructure/trpc/react";
import { AppLayout, PageBreadcrumb } from "@/shared/layout";
import { Button } from "@/shared/ui/button";
import {
  AgentRunStatus,
  AgentTypes,
  type AzureDevOpsPipelineAgentResult,
  type GitHubActionsAgentResult,
  type K8sAgentResult,
  type K8sOptimizationAgentResult,
  type OtelReceiverAgentResult,
  type SentryAgentResult,
} from "@vivus/common";
export const Route = createFileRoute(
  "/app/project/$id/agents/$agentId/runs/$runId/"
)({
  loader: async ({ context, params }) => {
    const [projectData, agentData] = await Promise.all([
      context.queryClient.query(
        context.trpc.project.getById.queryOptions({
          projectId: params.id,
        })
      ),
      context.queryClient.query(
        context.trpc.agents.getById.queryOptions({
          projectId: params.id,
          agentId: params.agentId,
        })
      ),
      context.queryClient
        .query(
          context.trpc.agents.getRunById.queryOptions({
            projectId: params.id,
            agentId: params.agentId,
            runId: params.runId,
          })
        )
        .catch(() => undefined),
      context.queryClient
        .query(
          context.trpc.agents.getRunGitHubIssues.queryOptions(
            {
              projectId: params.id,
              agentId: params.agentId,
              runId: params.runId,
            }
          )
        )
        .catch(() => undefined),
    ]);

    const isK8sAgent =
      agentData?.agent?.template?.type ===
        AgentTypes.K8sIssues ||
      agentData?.agent?.template?.type ===
        AgentTypes.K8sOptimization;

    if (projectData?.project?.organizationId) {
      const organizationId =
        projectData.project.organizationId;

      await Promise.all([
        context.queryClient
          .query(
            context.trpc.organization.getById.queryOptions({
              id: organizationId,
            })
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.getInstallation.queryOptions(
              { organizationId }
            )
          )
          .catch(() => undefined),
        ...(isK8sAgent
          ? [
              context.queryClient
                .query(
                  context.trpc.githubApp.getRepoMappings.queryOptions(
                    { organizationId }
                  )
                )
                .catch(() => undefined),
              context.queryClient
                .query(
                  context.trpc.githubApp.getVivusConfigStatus.queryOptions(
                    { organizationId }
                  )
                )
                .catch(() => undefined),
            ]
          : []),
      ]);
    }
  },
  component: RunDetailPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function RunDetailPage() {
  const {
    id: projectId,
    agentId,
    runId,
  } = Route.useParams();
  const trpc = useTRPC();

  const { data: projectData } = useSuspenseQuery(
    trpc.project.getById.queryOptions({ projectId })
  );

  const organizationId =
    projectData?.project?.organizationId;

  // Use useQuery with enabled to avoid N+1 query with empty string
  const { data: organizationData } = useQuery({
    ...trpc.organization.getById.queryOptions({
      id: organizationId ?? "",
    }),
    enabled: !!organizationId,
  });

  const { data: githubInstallation } = useQuery({
    ...trpc.githubApp.getInstallation.queryOptions({
      organizationId: organizationId ?? "",
    }),
    enabled: !!organizationId,
  });

  const { data: agentData } = useSuspenseQuery(
    trpc.agents.getById.queryOptions({ projectId, agentId })
  );

  const isK8sIssuesAgent =
    agentData?.agent?.template?.type ===
    AgentTypes.K8sIssues;
  const isK8sOptimizationAgent =
    agentData?.agent?.template?.type ===
    AgentTypes.K8sOptimization;
  const isAnyK8sAgent =
    isK8sIssuesAgent || isK8sOptimizationAgent;

  const { data: repoMappingsData } = useQuery({
    ...trpc.githubApp.getRepoMappings.queryOptions({
      organizationId: organizationId ?? "",
    }),
    enabled: isAnyK8sAgent && !!organizationId,
  });

  const { data: vivusConfigStatusData } = useQuery({
    ...trpc.githubApp.getVivusConfigStatus.queryOptions({
      organizationId: organizationId ?? "",
    }),
    enabled: isAnyK8sAgent && !!organizationId,
  });

  const now = useCurrentTime();
  const { data: runData } = useSuspenseQuery({
    ...trpc.agents.getRunById.queryOptions({
      projectId,
      agentId,
      runId,
    }),
    refetchInterval: (query) => {
      const run = query.state.data?.run;

      if (!run) return 5000;

      if (run.status !== AgentRunStatus.Completed) {
        return 5000;
      }

      if (!run.completedAt) return 5000;

      const completedAtMs = new Date(
        run.completedAt
      ).getTime();
      const ageMs = Date.now() - completedAtMs;

      // Allow late-arriving GitHub events/metrics to show up
      return ageMs < 120_000 ? 5000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const run = runData?.run;

  const shouldPollSideEffects =
    run?.status !== AgentRunStatus.Completed ||
    (run?.completedAt
      ? now - new Date(run.completedAt).getTime() < 120_000
      : true);

  const { data: githubIssuesData } = useSuspenseQuery({
    ...trpc.agents.getRunGitHubIssues.queryOptions({
      projectId,
      agentId,
      runId,
    }),
    refetchInterval: shouldPollSideEffects ? 5000 : false,
    refetchIntervalInBackground: false,
  });

  const orgName = String(
    organizationData?.organization?.name ?? ""
  );
  const projectName = String(
    projectData?.project?.name ?? ""
  );
  const orgId = String(
    projectData?.project?.organizationId ?? ""
  );
  const agentName = String(agentData?.agent?.name ?? "");
  const result = run?.result;
  const [showSuppressedItems, setShowSuppressedItems] =
    useState(false);

  const [previousRunId, setPreviousRunId] = useState(runId);
  if (previousRunId !== runId) {
    setPreviousRunId(runId);
    setShowSuppressedItems(false);
  }

  const k8sIssuesDisplayState = useMemo(() => {
    if (!result || !isK8sIssuesAgent) {
      return null;
    }

    return getK8sIssuesDisplayState({
      result: result as K8sAgentResult,
      repoMappings: repoMappingsData,
      showSuppressed: showSuppressedItems,
      vivusConfigStatus: vivusConfigStatusData,
    });
  }, [
    isK8sIssuesAgent,
    repoMappingsData,
    result,
    showSuppressedItems,
    vivusConfigStatusData,
  ]);

  const k8sOptimizationDisplayState = useMemo(() => {
    if (!result || !isK8sOptimizationAgent) {
      return null;
    }

    // Keep Optimization runs silently filtered for now. The Optimization UI
    // renders summaries and pod rows from `overprovisionedPods`, while the
    // filtered `issues` array is only used for issue-linking metadata, so the
    // K8s Issues show/hide toggle would not map cleanly to the visible rows.
    return filterSuppressedK8sIssuesForDisplay({
      result:
        result as unknown as K8sOptimizationAgentResult, // Safe: isK8sOptimizationAgent is the runtime guard; run.result stays a broader AgentRunResult union that TypeScript cannot narrow via the boolean flag.
      repoMappings: repoMappingsData,
      vivusConfigStatus: vivusConfigStatusData,
    });
  }, [
    isK8sOptimizationAgent,
    repoMappingsData,
    result,
    vivusConfigStatusData,
  ]);

  const suppressedCount =
    // The show/hide toggle is intentionally limited to K8s Issues runs.
    k8sIssuesDisplayState?.suppressedCount ?? 0;

  const githubIssues = githubIssuesData.issues;
  const duplicates = githubIssuesData.duplicates;
  const duplicateIssueIds =
    githubIssuesData.duplicateIssueIds ?? {};
  const skipStats = githubIssuesData.skipStats;
  const errors = githubIssuesData.errors ?? [];

  const isLoadingGitHubIssues =
    shouldPollSideEffects && githubIssues.length === 0;

  const handleIssueClick = useCallback((issue: Issue) => {
    const podId = getPodId(issue.namespace, issue.resource);
    const element = document.getElementById(podId);
    if (element) {
      if (
        element instanceof HTMLDetailsElement &&
        !element.open
      ) {
        element.open = true;
      }
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      element.classList.add(
        "ring-2",
        "ring-primary",
        "ring-offset-2"
      );
      setTimeout(() => {
        element.classList.remove(
          "ring-2",
          "ring-primary",
          "ring-offset-2"
        );
      }, 2000);
    }
  }, []);

  const breadcrumbItems = [
    {
      label: orgName,
      to: "/app/organization/$id",
      params: { id: orgId },
    },
    {
      label: projectName,
      to: "/app/project/$id",
      params: { id: projectId },
    },
    {
      label: agentName,
      to: "/app/project/$id/agents/$agentId",
      params: { id: projectId, agentId },
    },
    {
      label: "Run Report",
    },
  ];

  return (
    <AppLayout
      title={<PageBreadcrumb items={breadcrumbItems} />}
      headerActions={
        <Link
          to="/app/project/$id/agents/$agentId"
          params={{ id: projectId, agentId }}
        >
          <Button variant="outline" size="sm">
            <IconArrowLeft className="mr-2 size-4" />
            Back to Agent
          </Button>
        </Link>
      }
    >
      <RunOverviewCards
        status={String(run?.status ?? "unknown")}
        startedAt={run?.startedAt ?? null}
        completedAt={run?.completedAt ?? null}
        outcome={run?.outcome ?? null}
      />

      {/* Error card - extract from result.errors if present */}
      {run?.outcome === "failure" &&
        (
          run?.result as {
            errors?: Array<{ message: string }>;
          }
        )?.errors?.[0] && (
          <ErrorCard
            errorMessage={
              (
                run.result as {
                  errors?: Array<{ message: string }>;
                }
              ).errors?.[0]?.message ?? "Unknown error"
            }
          />
        )}

      {/* GitHub Issues Warnings */}
      {organizationId && (
        <GitHubIssuesWarning
          skipStats={skipStats}
          errorCount={errors.length}
          organizationId={organizationId}
          maxIssuesPerHour={
            githubInstallation?.maxIssuesPerHour
          }
        />
      )}

      {suppressedCount > 0 && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowSuppressedItems((current) => !current);
            }}
          >
            {showSuppressedItems
              ? `Hide suppressed (${suppressedCount})`
              : `Show suppressed (${suppressedCount})`}
          </Button>
        </div>
      )}

      {/* Azure DevOps Pipeline Agent Results */}
      {agentData?.agent?.template?.type ===
        AgentTypes.AzureDevOpsPipeline &&
        result && (
          <AzureDevOpsSection
            result={
              result as unknown as AzureDevOpsPipelineAgentResult
            }
            githubIssues={githubIssues}
            duplicates={duplicates}
            duplicateIssueIds={duplicateIssueIds}
          />
        )}

      {/* GitHub Actions Agent Results */}
      {agentData?.agent?.template?.type ===
        AgentTypes.GitHubActions &&
        result && (
          <GitHubActionsResultSection
            result={
              result as unknown as GitHubActionsAgentResult
            }
            githubIssues={githubIssues}
            duplicates={duplicates}
            duplicateIssueIds={duplicateIssueIds}
          />
        )}

      {/* K8s Issues Agent Results */}
      {agentData?.agent?.template?.type ===
        AgentTypes.K8sIssues &&
        k8sIssuesDisplayState && (
          <K8sIssuesResultSection
            result={k8sIssuesDisplayState.displayResult}
            githubIssues={githubIssues}
            duplicates={duplicates}
            duplicateIssueIds={duplicateIssueIds}
            isLoadingGitHubIssues={isLoadingGitHubIssues}
            onIssueClick={handleIssueClick}
          />
        )}

      {/* K8s Optimization Agent Results */}
      {agentData?.agent?.template?.type ===
        AgentTypes.K8sOptimization &&
        k8sOptimizationDisplayState && (
          <K8sOptimizationResultSection
            result={k8sOptimizationDisplayState}
            githubIssues={githubIssues}
            duplicates={duplicates}
            duplicateIssueIds={duplicateIssueIds}
            isLoadingGitHubIssues={isLoadingGitHubIssues}
          />
        )}

      {/* Sentry Agent Results */}
      {agentData?.agent?.template?.type ===
        AgentTypes.Sentry &&
        result && (
          <SentryResultSection
            result={result as unknown as SentryAgentResult}
            githubIssues={githubIssues}
            duplicates={duplicates}
            duplicateIssueIds={duplicateIssueIds}
          />
        )}

      {/* OTel Receiver Agent Results */}
      {agentData?.agent?.template?.type ===
        AgentTypes.OtelReceiver &&
        result && (
          <OtelReceiverResultSection
            result={
              result as unknown as OtelReceiverAgentResult
            }
            githubIssues={githubIssues}
            duplicates={duplicates}
            duplicateIssueIds={duplicateIssueIds}
          />
        )}

      {!result && run?.status !== AgentRunStatus.Failed && (
        <PendingMessage
          status={String(run?.status ?? "")}
        />
      )}
    </AppLayout>
  );
}
